import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

function normalizeUrl(input: string): string {
  const trimmed = input.replace(/\/+/g, "/");
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown,
): Promise<Response> {
  const normalized = normalizeUrl(url);

  const headers: Record<string, string> = {};
  if (data !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  // With the Vite proxy in place, we can now use relative URLs for all API requests.
  // Credentials will be included by default because the requests are now same-origin.
  const res = await fetch(normalized, {
    method,
    headers,
    body: data !== undefined ? JSON.stringify(data) : undefined,
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";

function urlFromQueryKey(queryKey: readonly unknown[]): string {
  const parts = queryKey.map((p) => String(p));
  const joined = parts.join("/").replace(/\/+/g, "/");
  return joined.startsWith("/") ? joined : `/${joined}`;
}

export const getQueryFn = <T = unknown>(options: { on401: UnauthorizedBehavior }): QueryFunction<T, readonly unknown[]> => {
  return async (context) => {
    const queryKey = Array.isArray(context.queryKey) ? context.queryKey : [String(context.queryKey)];
    const url = urlFromQueryKey(queryKey);

    const res = await fetch(normalizeUrl(url));

    if (options.on401 === "returnNull" && res.status === 401) {
      return null as unknown as T;
    }

    await throwIfResNotOk(res);
    return (await res.json()) as T;
  };
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
